import React, { useState } from 'react';

interface BrokerRevenue {
  brokerId: string;
  brokerName: string;
  totalCents: number;
  percentOfTotal: number;
}

interface ConcentrationAlertProps {
  broker: BrokerRevenue;
  threshold?: number; // Default 25%
}

const ConcentrationAlert: React.FC<ConcentrationAlertProps> = ({
  broker,
  threshold = 25,
}) => {
  const [dismissed, setDismissed] = useState(false);

  // Don't show if dismissed or no concentration risk
  if (dismissed || !broker || broker.percentOfTotal <= threshold) {
    return null;
  }

  return (
    <div className="bg-yellow-900/20 border border-yellow-500/50 rounded-lg p-4 mb-6 relative">
      {/* Warning icon */}
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <svg
            className="h-6 w-6 text-yellow-400"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
              clipRule="evenodd"
            />
          </svg>
        </div>

        <div className="flex-1">
          <h3 className="text-yellow-300 font-semibold text-sm mb-1">
            High Revenue Concentration Risk
          </h3>
          <p className="text-yellow-200 text-sm">
            <strong>{broker.brokerName}</strong> represents{' '}
            <strong>{broker.percentOfTotal.toFixed(1)}%</strong> of total company revenue.
            This creates significant business risk if this broker were to leave or underperform.
          </p>
          <p className="text-yellow-300/70 text-xs mt-2">
            Industry best practice: No single broker should exceed {threshold}% of total revenue.
          </p>
        </div>

        {/* Dismiss button */}
        <button
          onClick={() => setDismissed(true)}
          className="flex-shrink-0 text-yellow-400 hover:text-yellow-300 transition-colors"
          aria-label="Dismiss alert"
        >
          <svg
            className="h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ConcentrationAlert;
